import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, Inbox, FolderKanban, Layers, Film, HelpCircle, FileText, Search, Mail, Users, LogOut, Menu, ExternalLink, UserPlus, Files } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/site/Wordmark";
import { Spinner } from "@/components/site/Primitives";

const NAV = [
  ["Operations", [["/admin", "Overview", LayoutGrid, true], ["/admin/enquiries", "Enquiries", Inbox], ["/admin/projects", "Projects", FolderKanban], ["/admin/applications", "Applications", UserPlus]]],
  ["Content", [["/admin/services", "Services & pricing", Layers], ["/admin/pages", "Service pages", Files], ["/admin/portfolio", "Portfolio", Film], ["/admin/faqs", "FAQs", HelpCircle], ["/admin/content", "Site content", FileText], ["/admin/seo", "SEO", Search]]],
  ["System", [["/admin/emails", "Emails", Mail], ["/admin/team", "Team & access", Users]]],
];

const Sidebar = ({ user, logout, close }) => (
  <aside className="w-64 shrink-0 border-r border-[#27272A] bg-[#050505] flex flex-col h-full">
    <div className="h-16 flex items-center px-6 border-b border-[#27272A]"><Wordmark size="text-xl" /><span className="ml-2 eb-label !text-[10px]">Admin</span></div>
    <nav className="flex-1 overflow-auto py-4">
      {NAV.map(([group, items]) => (
        <div key={group} className="mb-4">
          <p className="px-6 py-2 eb-label !text-[10px] !text-zinc-600">{group}</p>
          {items.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} onClick={close} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors ${isActive ? "border-[#0055FF] bg-[#0055FF]/10 text-white" : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5"}`}>
              <Icon size={16} />{label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
    <div className="border-t border-[#27272A] p-4 space-y-3">
      <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-zinc-500 hover:text-white" data-testid="admin-view-site"><ExternalLink size={14} />View public site</a>
      <div className="flex items-center justify-between">
        <div className="min-w-0"><p className="text-sm truncate" data-testid="admin-user-name">{user.name}</p><p className="text-xs text-zinc-600 uppercase tracking-wider">{user.role}</p></div>
        <button onClick={logout} className="p-2 text-zinc-500 hover:text-white" aria-label="Sign out" data-testid="admin-logout"><LogOut size={16} /></button>
      </div>
    </div>
  </aside>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-[#050505] text-zinc-500"><Spinner /></div>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  return (
    <div className="h-screen flex bg-[#050505] text-white font-body" data-testid="admin-layout">
      <div className="hidden lg:block"><Sidebar user={user} logout={logout} /></div>
      {open && <div className="fixed inset-0 z-50 flex lg:hidden"><Sidebar user={user} logout={logout} close={() => setOpen(false)} /><div className="flex-1 bg-black/70" onClick={() => setOpen(false)} /></div>}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden h-14 flex items-center justify-between px-4 border-b border-[#27272A]">
          <Wordmark size="text-lg" />
          <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-mobile-menu"><Menu size={20} /></button>
        </div>
        <main className="flex-1 overflow-auto p-5 md:p-8 lg:p-10"><Outlet /></main>
      </div>
    </div>
  );
}
